
import { useEffect, useRef } from 'react';

interface ConfettiProps {
  duration?: number;
}

export default function ConfettiCanvas({ duration = 10000 }: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Store canvas and context as non-null variables after the checks
    const canvasElement = canvas;
    const context = ctx;

    const particles: {
      x: number;
      y: number;
      size: number;
      color: string;
      speed: number;
      opacity: number;
      fadeSpeed: number;
    }[] = [];

    // Create particles
    const particleCount = 150;
    const colors = ['#FF5252', '#FFD740', '#64FFDA', '#448AFF', '#E040FB'];

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvasElement.width,
        y: Math.random() * canvasElement.height - canvasElement.height,
        size: Math.random() * 8 + 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        speed: Math.random() * 5 + 3, // Increased speed range from 1-4 to 3-8
        opacity: 1,
        fadeSpeed: Math.random() * 0.005 + 0.002 // Slightly increased fade speed too
      });
    }

    let animationFrame: number;
    const endTime = Date.now() + duration;

    function animate() {
      context.clearRect(0, 0, canvasElement.width, canvasElement.height);

      particles.forEach((p, index) => {
        p.y += p.speed;
        
        // Gradually reduce opacity
        p.opacity -= p.fadeSpeed;
        
        // Remove particles that are completely faded
        if (p.opacity <= 0) {
          particles.splice(index, 1);
          return;
        }

        context.beginPath();
        context.globalAlpha = p.opacity;
        context.fillStyle = p.color;
        context.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        context.fill();
        context.globalAlpha = 1;
      });

      // Stop animation after duration or when all particles have faded
      if (Date.now() < endTime && particles.length > 0) {
        animationFrame = requestAnimationFrame(animate);
      }
    }

    animate();

    const handleResize = () => {
      canvasElement.width = window.innerWidth;
      canvasElement.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrame);
    };
  }, [duration]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 pointer-events-none z-50"
      style={{ width: '100vw', height: '100vh' }}
    />
  );
}
